import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import UnifiedHeader from '../layout/UnifiedHeader';
import Footer from '../layout/Footer';
import { destroyScrollAnimations } from '../../utils/optimizedAnimations';
import HashScroller from '../utils/HashScroller';
import { setJsonLd } from '../../utils/seo';

// Import sections directly to avoid flash
import HeroSection from '../sections/HeroSection';
import IntroSection from '../sections/IntroSection';
import ServicesSection from '../sections/ServicesSection';
import CaseStudiesSection from '../sections/CaseStudiesSection';
import WhyChooseUsSection from '../sections/WhyChooseUsSection';
import PricingSection from '../sections/PricingSection';
import TestimonialsSection from '../sections/TestimonialsSection';
import AboutSection from '../sections/AboutSection';
import FaqSection from '../sections/FaqSection';
import BlogSection from '../sections/BlogSection';

const HomePage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Inject Organization + LocalBusiness JSON-LD
    try {
      const origin = window.location.origin;
      setJsonLd({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Paokhum",
        "url": origin,
        "logo": origin + "/Paokhum_logo_favicon.svg"
      });
    } catch {}

    // DISABLED - Initialize optimized scroll animations
    // initScrollAnimations();

    // Handle hash navigation after page loads
    const handleHashNavigation = () => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        
        // Try multiple times with increasing delays to ensure element is found
        const attempts = [100, 300, 600, 1000];
        let attemptIndex = 0;
        
        const tryScroll = () => {
          const element = document.getElementById(id);
          if (element) {
            // Add offset for fixed header
            const offset = 80;
            const elementPosition = element.offsetTop - offset;
            window.scrollTo({
              top: elementPosition,
              behavior: 'smooth'
            });
          } else if (attemptIndex < attempts.length) {
            // Try again with next delay
            setTimeout(tryScroll, attempts[attemptIndex]);
            attemptIndex++;
          } else {
            // Element not found after all attempts — silently give up
          }
        };
        
        // Start trying to scroll
        tryScroll();
      }
    };

    // Small initial delay to let React render
    const timer = setTimeout(handleHashNavigation, 50);

    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      destroyScrollAnimations();
    };
  }, [location]);

  return (
    <div className="relative">
      <HashScroller />
      <UnifiedHeader />
      <main id="main-content" className="min-h-screen relative">
        <HeroSection />
        <IntroSection />
        <ServicesSection />
        <CaseStudiesSection />
        <WhyChooseUsSection />
        <PricingSection />
        <TestimonialsSection />
        <AboutSection />
        <FaqSection />
        <BlogSection />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
